import { useState, useEffect, useCallback } from 'react';
import { supabase, isSupabaseEnabled } from '../lib/supabase';
import { clearAuthSessionCache, getAccessToken as getCurrentAccessToken } from '../lib/authSession';
import { clearMobileSessionState, isMobileSessionRevokedError, registerMobileSession } from '../lib/mobileSessionSecurity';
import { store } from '../utils/store';
import { AuthContext, AUTH_REDIRECT_NEXT_KEY } from './authContextValue';

const CACHED_USER_KEY = 'shadowland_user';

function cacheUser(u) {
  try { localStorage.setItem(CACHED_USER_KEY, JSON.stringify(u)); } catch { /* ignore unavailable storage */ }
}
function getCachedUser() {
  try { const s = localStorage.getItem(CACHED_USER_KEY); return s ? JSON.parse(s) : null; } catch { return null; }
}
function clearCachedUser() {
  try { localStorage.removeItem(CACHED_USER_KEY); } catch { /* ignore unavailable storage */ }
}

function looksLikeExpiredRefreshToken(err) {
  const msg = String(err?.message || err?.error_description || err || '');
  return /Invalid Refresh Token|Refresh Token.*Expired|Session Expired/i.test(msg);
}

function clearSupabaseAuthStorage() {
  clearAuthSessionCache();
  clearMobileSessionState();
  clearCachedUser();
  try { localStorage.removeItem('shadowland-auth'); } catch { /* ignore unavailable storage */ }
  try { sessionStorage.removeItem('shadowland-auth'); } catch { /* ignore unavailable storage */ }
}

function isAccessDeniedError(err) {
  return err?.code === 'INVITE_REQUIRED' || /invite required|approved account/i.test(String(err?.message || ''));
}

function isProfileTimeoutError(err) {
  return /timed out after/i.test(String(err?.message || err || ''));
}

function redirectToInviteRequiredLogin() {
  if (typeof window === 'undefined') return;
  const target = '/login?auth_error=Admin%20invite%20required';
  if (window.location.pathname !== '/login' || !window.location.search.includes('Admin%20invite%20required')) {
    window.location.replace(target);
  }
}

function redirectToSessionRevokedLogin() {
  if (typeof window === 'undefined') return;
  const target = '/login?auth_error=Mobile%20session%20revoked';
  if (window.location.pathname !== '/login' || !window.location.search.includes('Mobile%20session%20revoked')) {
    window.location.replace(target);
  }
}

function avatarUrlFromAuth(authUser) {
  const meta = authUser?.user_metadata || {};
  return meta.avatar_url || meta.picture || '';
}

// Key-order-independent structural compare. The user object is flat
// and JSON-safe (see buildSupabaseUser), and one side may have come
// back through localStorage, so a plain JSON.stringify comparison
// would be at the mercy of key order.
function stableStringify(value) {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  const keys = Object.keys(value).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(value[k])}`).join(',')}}`;
}

function sameUser(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  return stableStringify(a) === stableStringify(b);
}

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(() => getCachedUser());
  const [loading, setLoading] = useState(() => (isSupabaseEnabled ? true : !getCachedUser()));

  const setUserAndCache = (u) => {
    // Supabase re-fires onAuthStateChange on TOKEN_REFRESHED (roughly
    // hourly) and on some tab-focus events. buildSupabaseUser returns a
    // brand-new object each time, and that new *identity* alone is
    // enough to invalidate AppContext's loadAll callback — which
    // refetches every deal, contact, document and pricing row, and
    // tears down and rebuilds all ten realtime channels. Nothing about
    // the user actually changed. Hold the previous reference when the
    // contents match so a token refresh stays invisible to the app.
    setUser((prev) => (sameUser(prev, u) ? prev : u));
    if (u) cacheUser(u);
    else clearCachedUser();
  };

  const refreshUser = useCallback(async () => {
    const { data: { session } = {} } = await supabase.auth.getSession();
    if (!session?.user) return null;
    const freshUser = await buildSupabaseUser(session.user);
    await registerApprovedMobileSession();
    setUserAndCache(freshUser);
    return freshUser;
  // buildSupabaseUser is a function declaration below; including it would
  // recreate this callback every render without changing auth behavior.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Bootstrap ────────────────────────────────────────────────────────────

  useEffect(() => {
    if (isSupabaseEnabled) {
      const safetyTimer = setTimeout(() => setLoading(false), 4000);

      supabase.auth.getSession().then(async ({ data: { session } = {}, error }) => {
        try {
          if (error) throw error;
          if (session?.user) {
            let freshUser;
            try { freshUser = await buildSupabaseUser(session.user); }
            catch (err) {
              console.warn('[Auth] buildSupabaseUser failed during bootstrap:', err?.message);
              clearSupabaseAuthStorage();
              try { await supabase.auth.signOut(); } catch { /* ignore signout failure */ }
              setUserAndCache(null);
              if (isAccessDeniedError(err)) {
                redirectToInviteRequiredLogin();
                return;
              }
              throw err;
            }
            try { await registerApprovedMobileSession(); }
            catch (err) {
              if (isMobileSessionRevokedError(err)) {
                clearSupabaseAuthStorage();
                try { await supabase.auth.signOut(); } catch { /* ignore signout failure */ }
                setUserAndCache(null);
                redirectToSessionRevokedLogin();
                return;
              }
              console.warn('[Auth] mobile session registration skipped:', err?.message);
            }
            setUserAndCache(freshUser);
          } else {
            setUserAndCache(null);
          }
        } catch (err) {
          console.error('[Auth] Failed to build user from session:', err);
          if (looksLikeExpiredRefreshToken(err)) {
            clearSupabaseAuthStorage();
            setUser(null);
          }
        } finally {
          setLoading(false);
          clearTimeout(safetyTimer);
        }
      }).catch((err) => {
        console.error('[Auth] getSession failed during bootstrap:', err);
        if (looksLikeExpiredRefreshToken(err)) {
          clearSupabaseAuthStorage();
          setUser(null);
        }
        setLoading(false);
        clearTimeout(safetyTimer);
      });

      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        async (event, session) => {
          try {
            if (event === 'SIGNED_OUT') {
              setUserAndCache(null);
            } else if (session?.user) {
              let freshUser;
              try { freshUser = await buildSupabaseUser(session.user); }
              catch (err) {
                console.warn('[Auth] buildSupabaseUser failed in onAuthStateChange:', err?.message);
                clearSupabaseAuthStorage();
                try { await supabase.auth.signOut(); } catch { /* ignore signout failure */ }
                setUserAndCache(null);
                if (isAccessDeniedError(err)) redirectToInviteRequiredLogin();
                return;
              }
              try { await registerApprovedMobileSession(); }
              catch (err) {
                if (isMobileSessionRevokedError(err)) {
                  clearSupabaseAuthStorage();
                  try { await supabase.auth.signOut(); } catch { /* ignore signout failure */ }
                  setUserAndCache(null);
                  redirectToSessionRevokedLogin();
                  return;
                }
                console.warn('[Auth] mobile session registration skipped:', err?.message);
              }
              setUserAndCache(freshUser);
            }
          } catch (err) {
            console.error('[Auth] Auth state change error:', err);
          } finally {
            setLoading(false);
          }
        }
      );

      return () => {
        clearTimeout(safetyTimer);
        subscription.unsubscribe();
      };
    } else {
      store.seedDefaults?.();
      const saved = store.get(store.KEYS.CURRENT_USER);
      if (saved) setUser(saved);
      setLoading(false);
    }
  // Bootstrap must run once per provider mount; auth state changes are handled
  // by Supabase's subscription inside the effect.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Helpers ──────────────────────────────────────────────────────────────

  // Per-call cap for the raw fetches below. We use plain fetch for
  // profile bootstrap so Supabase client queueing/token-refresh work
  // cannot hold up the app shell.
  const PROFILE_CALL_TIMEOUT_MS = 5000;
  const withProfileTimeout = (p, label) => Promise.race([
    p,
    new Promise((_, reject) => setTimeout(
      () => reject(new Error(`${label} timed out after ${PROFILE_CALL_TIMEOUT_MS / 1000}s`)),
      PROFILE_CALL_TIMEOUT_MS,
    )),
  ]);

  const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
  const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

  async function registerApprovedMobileSession() {
    const result = await registerMobileSession();
    if (result?.session?.revokedAt) {
      const err = new Error('Mobile session revoked');
      err.code = 'MOBILE_SESSION_REVOKED';
      throw err;
    }
    return result;
  }

  async function buildSupabaseUser(authUser) {
    // Prefer the direct profiles endpoint. The get_my_profile RPC is
    // a compatibility fallback only; starting it in parallel caused
    // harmless 5s timeout warnings even after direct profile loading
    // had already succeeded.
    if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
      const err = new Error('Supabase env not configured; cannot verify approved account.');
      err.code = 'INVITE_REQUIRED';
      throw err;
    }
    const token = await getCurrentAccessToken({ timeoutMs: 2500 });
    if (!token) {
      const err = new Error('No session token available; cannot verify approved account.');
      err.code = 'INVITE_REQUIRED';
      throw err;
    }
    const headers = {
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${token}`,
    };

    const fetchRpcProfile = async () => {
      try {
        const r = await withProfileTimeout(
          fetch(`${SUPABASE_URL}/rest/v1/rpc/get_my_profile`, {
            method: 'POST',
            headers: { ...headers, 'Content-Type': 'application/json' },
            body: '{}',
          }),
          'get_my_profile RPC',
        );
        if (!r.ok) {
          console.warn('[Auth] RPC get_my_profile non-OK:', r.status);
          return null;
        }
        const data = await r.json().catch(() => null);
        if (!data) return null;
        return Array.isArray(data) ? data[0] || null : data;
      } catch (err) {
        if (!isProfileTimeoutError(err)) {
          console.warn('[Auth] RPC get_my_profile exception:', err?.message);
        }
        return null;
      }
    };

    const fetchDirectProfile = async () => {
      try {
        const r = await withProfileTimeout(
          fetch(
            `${SUPABASE_URL}/rest/v1/profiles?id=eq.${encodeURIComponent(authUser.id)}&select=*`,
            { headers },
          ),
          'profiles select',
        );
        if (!r.ok) {
          console.warn('[Auth] Direct profile query non-OK:', r.status);
          return null;
        }
        const rows = await r.json().catch(() => null);
        if (!Array.isArray(rows) || rows.length === 0) return null;
        return rows[0];
      } catch (err) {
        if (!isProfileTimeoutError(err)) {
          console.warn('[Auth] Direct profile query exception:', err?.message);
        }
        return null;
      }
    };

    // Direct profile rows include the full authorization shape
    // (`role`, `permissions`, `tenant_id`). Only fall back to the RPC
    // if direct profile loading is unavailable.
    let profile = await fetchDirectProfile();
    if (!profile) profile = await fetchRpcProfile();

    if (!profile) {
      const err = new Error('Invite required. Ask an admin to create your account before signing in.');
      err.code = 'INVITE_REQUIRED';
      throw err;
    }

    // The photo the identity provider just handed back. Mirrored onto the
    // profile when it has drifted, because auth.users is not readable from
    // a browser — so without this copy, teammates can only ever be
    // initials to each other. Best effort: a failed write leaves the older
    // photo in place, which is a stale picture rather than a broken login.
    const liveAvatar = avatarUrlFromAuth(authUser);
    if (liveAvatar && liveAvatar !== (profile?.avatar_url || '')) {
      supabase.from('profiles').update({ avatar_url: liveAvatar }).eq('id', authUser.id)
        .then(({ error }) => {
          if (error) console.warn('[auth] could not mirror the profile photo:', error.message);
        });
    }

    return {
      id:       authUser.id,
      email:    authUser.email,
      name:     profile?.name || authUser.user_metadata?.name || authUser.email,
      avatarUrl: liveAvatar || profile?.avatar_url || '',
      phone:    profile?.phone || authUser.user_metadata?.phone || '',
      company:  profile?.company || authUser.user_metadata?.company || '',
      role:     profile?.role || 'landman',
      // Job title shown in offer signature blocks ("Transaction
      // Coordinator") — editable by admins in User Accounting.
      jobTitle: profile?.job_title || '',
      // Team position + commission rate on margin (fraction), set when
      // the admin invited this user.
      position: profile?.position || '',
      commissionRate: profile?.commission_rate == null ? null : Number(profile.commission_rate),
      permissions: Array.isArray(profile?.permissions) ? profile.permissions : [],
      onboarding: (profile?.onboarding && typeof profile.onboarding === 'object') ? profile.onboarding : {},
      savedSignatures: Array.isArray(profile?.saved_signatures) ? profile.saved_signatures : [],
      // Tenant id loaded from the user's profile. Surfaced on the
      // user object so consumers (TenantContext, components that
      // need to filter or stamp tenant_id explicitly) can read it
      // without a second profile query. Null for users whose row
      // hasn't been backfilled yet — RLS will reject their writes
      // until an admin sets it.
      tenantId: profile?.tenant_id || null,
    };
  }

  // ── SIGN IN with Google (OAuth) ─────────────────────────────────────────
  // Hands off to Supabase's hosted OAuth flow — the browser navigates
  // to Google's consent screen and back via PKCE. The session lands
  // in localStorage under our configured `shadowland-auth` key, and
  // onAuthStateChange picks it up to drive setUserAndCache.
  //
  // Requires the Google provider to be enabled in
  // Supabase Dashboard → Authentication → Providers → Google
  // with the OAuth client credentials filled in. Without that, the
  // redirect lands on Supabase's "provider not enabled" error page.
  const signInWithGoogle = async () => {
    if (!isSupabaseEnabled) {
      return { success: false, error: 'Supabase not configured' };
    }
    try {
      clearAuthSessionCache();
      // Nothing is stored here on purpose. This used to stash '/deals',
      // which meant a Google sign-in ignored wherever the app's landing
      // page had been moved to and arrived on the Deal Tracker anyway.
      // With the key unset the callback falls back to DEFAULT_APP_PATH,
      // so there is one answer to "where does signing in go". The key
      // stays available for carrying a real deep link through the round
      // trip, which is what it is for.
      try { sessionStorage.removeItem(AUTH_REDIRECT_NEXT_KEY); } catch { /* ignore unavailable storage */ }
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          // Bring the user straight to the callback route. Routing
          // through "/" lets app bootstrap and redirects race the
          // PKCE exchange, which can reuse/expire provider codes.
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            prompt: 'select_account',
          },
        },
      });
      if (error) return { success: false, error: error.message };
      // signInWithOAuth triggers a full-page redirect, so this
      // return value usually isn't observed by the caller.
      return { success: true };
    } catch (err) {
      return { success: false, error: err?.message || 'Google sign-in failed.' };
    }
  };

  // ── LOGOUT ──────────────────────────────────────────────────────────────

  const logout = async () => {
    setUser(null);
    clearAuthSessionCache();
    clearMobileSessionState();
    clearCachedUser();
    try { localStorage.removeItem(store.KEYS?.CURRENT_USER); } catch { /* ignore unavailable storage */ }
    // Tear down every realtime subscription before the hard redirect.
    // Without this, the old AppContext channels keep delivering
    // postgres_changes / presence updates to a logged-out tab — the
    // resulting setState calls land on unmounted components and ghost
    // updates surface if the user logs back in on the same tab.
    try {
      const channels = (typeof supabase.getChannels === 'function')
        ? supabase.getChannels()
        : [];
      await Promise.all(
        channels.map((ch) => {
          try { return supabase.removeChannel(ch); }
          catch { return null; }
        }),
      );
    } catch { /* ignore channel teardown failures during logout */ }
    try { await supabase.auth.signOut(); } catch { /* redirect even if remote signout fails */ }
    window.location.href = '/login';
  };

  // ── All users have full access ──────────────────────────────────────────

  const hasPermission = () => true;

  return (
    <AuthContext.Provider value={{ user, loading, refreshUser, signInWithGoogle, logout, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
}
